import { Box, ListItem, ListItemText, Typography, Chip, IconButton, Tooltip, Checkbox } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import { SimpleDataBoxRenderer } from "../components/SimpleDataBoxRenderer";
import { TinyValueComponent } from "../components/TinyValueComponent";
import { format } from "date-fns";
import { ConnectedInboxData } from "../../../../src/documents/email/types";

interface EmailListItemProps {
  subject: string;
  date: string | Date;
  id: string;
  emailId?: string | null; // The database email ID if stored
  additionalData?: any;
  isSelected: boolean;
  onSelect: () => void;
  threadId?: string;
  tags?: string[];
  snippet?: string;
  headerMessageId?: string;
  connectedInboxesData?: ConnectedInboxData[]; // Array of connected inbox data
  onInboxChipClick?: (inboxData: ConnectedInboxData) => void; // Callback for clicking inbox chips
  onCopyLink?: () => void; // Callback for copying link
  // Multi-select mode props
  selectionMode?: boolean; // Show checkbox for selection
  isChecked?: boolean; // Checkbox state
  onToggleCheck?: () => void; // Toggle checkbox handler
}
export const EmailListItem: React.FC<
  EmailListItemProps & { sender?: string }
> = ({
  subject,
  date,
  id,
  emailId,
  threadId,
  headerMessageId,
  additionalData,
  isSelected,
  onSelect,
  sender,
  tags,
  snippet,
  connectedInboxesData,
  onInboxChipClick,
  onCopyLink,
  selectionMode,
  isChecked,
  onToggleCheck,
}) => {
  return (
    <Box>
      <ListItem
        onClick={onSelect}
        sx={{
          backgroundColor: isSelected ? "#e3f2fd" : "inherit",
          "&:hover": { backgroundColor: "#f1f1f1" },
          cursor: "pointer",
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        {selectionMode && (
          <Checkbox
            checked={isChecked}
            onChange={(e) => {
              e.stopPropagation();
              onToggleCheck?.();
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{ mr: 1, alignSelf: "flex-start", mt: 1, p: 0.5 }}
            size="small"
          />
        )}
        <ListItemText
          primary={
            <Typography variant="subtitle2" noWrap>
              {subject}
            </Typography>
          }
          secondary={
            <>
              <Typography variant="caption" color="text.secondary">
                {format(new Date(date), "MMM dd, yyyy hh:mm a")}
                {sender && ` - ${sender}`}
                <br />
                {threadId && (
                  <TinyValueComponent
                    prefix="tid: "
                    cropText
                    value={threadId}
                    clipboard
                  />
                )}
                <TinyValueComponent
                  cropText
                  value={id}
                  clipboard
                  prefix="mid: "
                />
                {emailId && (
                  <TinyValueComponent
                    prefix="emailId: "
                    value={emailId}
                    clipboard
                    cropText
                  />
                )}
                {headerMessageId && (
                  <TinyValueComponent
                    prefix="hmid:"
                    value={headerMessageId}
                    clipboard
                    cropText
                  />
                )}
                <br />
                {/* Tags */}
                <Box display="flex" gap={0.5} pt={0.5}>
                  {tags &&
                    tags.map((t) => (
                      <TinyValueComponent key={t} value={t} autocolor />
                    ))}
                </Box>
                {/* Inbox chips */}
                {connectedInboxesData && connectedInboxesData.length > 0 && (
                  <Box display="flex" gap={0.5} pt={0.5}>
                    {connectedInboxesData.map((inboxData) => (
                      <Chip
                        key={inboxData.inboxEmail}
                        label={inboxData.inboxEmail}
                        size="small"
                        variant="outlined"
                        clickable={!!onInboxChipClick}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent list item selection
                          onInboxChipClick?.(inboxData);
                        }}
                        sx={{
                          fontSize: '0.7rem',
                          height: '20px',
                          bgcolor: 'rgba(25, 118, 210, 0.08)',
                          borderColor: 'rgba(25, 118, 210, 0.23)',
                          color: 'rgba(25, 118, 210, 0.87)',
                          cursor: onInboxChipClick ? 'pointer' : 'default',
                          '&:hover': onInboxChipClick ? {
                            bgcolor: 'rgba(25, 118, 210, 0.15)',
                          } : {},
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Typography>
              {snippet && (
                <Typography variant="caption" color="text.secondary">
                  {snippet}
                </Typography>
              )}
              {additionalData && (
                <SimpleDataBoxRenderer sx={{ mt: 1 }} data={additionalData} />
              )}
            </>
          }
        />
        {onCopyLink && (
          <Box sx={{ ml: 1, mt: 1 }}>
            <Tooltip title="Copy link to this message">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyLink();
                }}
                sx={{ p: 0.5 }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </ListItem>
    </Box>
  );
};
